import React, { useState, useEffect, useRef } from 'react';
import NudosSearchBar from '../../NudosSearchBar/NudosSearchBar';
import { IbackofficeUserData } from '../../../../types/configuration.types';
import BordOneToneIcon from '../../BordOneToneIcon/BordOneToneIcon';
import NudosButton from '../../NudosButton/NudosButton';
import { IResponsibleUserData } from '../../../../types/responsibleUser.types';
import NudosConfirmActionModal from '../../NudosConfirmActionModal/NudosConfirmActionModal';
import BordPhoto from '../../BordPhoto/BordPhoto';
import './BordResponsibleUserSearchDropdown.scss';

/**
 * Dropdown con buscador para asignar o quitar responsables.
 * @param users Lista de empleados disponibles.
 * @param responsibleUsersList Responsables actualmente asignados.
 * @param handleAssignResponsible Función para agregar responsable.
 * @param handleDeleteResponsible Función para quitar responsable.
 * @param handleCloseDropdown Función para cerrar el dropdown.
 * @param isLoading Booleano para indicar si se está cargando.
 * @param logisticServiceId Número del servicio logístico para mostrar en el modal de confirmación.
 * @param orderId Número de la orden para mostrar en el modal de confirmación.
 */
const BordResponsibleUserSearchDropdown: React.FC<BordResponsibleUserSearchDropdownProps> = ({
  users,
  responsibleUsersList,
  handleAssignResponsible,
  handleDeleteResponsible,
  handleCloseDropdown,
  isLoading,
  logisticServiceId,
  orderId
}) => {
  const [searchUserText, setSearchUserText] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ responsibleId?: number; name?: string } | null>(null);

  const ref = useRef<HTMLDivElement>(null);
  const filteredUsers = users.filter(
    user =>
      user.firstName?.toLowerCase().includes(searchUserText.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchUserText.toLowerCase())
  );

  const confirmActionText = (
    <div>
      Estás a punto de eliminar a {userToDelete?.name} como responsable{' '}
      {logisticServiceId ? 'del servicio' : 'de la orden'} N°{logisticServiceId ? logisticServiceId : orderId}
      <br />
      ¿Seguro que deseas continuar?
    </div>
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) handleCloseDropdown();
  };

  const openDeleteModal = (responsibleId?: number, name?: string) => {
    setUserToDelete({ responsibleId, name });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const confirmDeleteResponsible = () => {
    handleDeleteResponsible(userToDelete?.responsibleId, userToDelete?.name);
    closeDeleteModal();
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleCloseDropdown]);

  return (
    <div className="bordResponsibleUserSearchDropdown" ref={ref}>
      {showDeleteModal && (
        <NudosConfirmActionModal
          modalText={confirmActionText}
          firstButtonText="Cancelar"
          secondButtonText="Eliminar responsable"
          firstButtonAction={closeDeleteModal}
          secondButtonAction={confirmDeleteResponsible}
          customHeight="220px"
        />
      )}

      <NudosSearchBar
        handleChange={setSearchUserText}
        placeholder="Busca por nombre o correo"
        customClassName="searchBarComponent"
        componentSize="medium"
        includeMagnifierIcon
      />

      <div className="userList">
        {filteredUsers.map((user, index) => {
          const userIsResponsible = responsibleUsersList.find(responsibleUser => responsibleUser.userId === user.id);
          const handleClick = () =>
            userIsResponsible ? openDeleteModal(userIsResponsible?.id, user.firstName) : handleAssignResponsible(user);
          return (
            <div className="optionContainer" key={`${user.email}-${index}`}>
              <div className="userInfo">
                <BordPhoto
                  url={user?.photoProfile}
                  variant="circle"
                  size="s20"
                  avatarProps={{
                    variant: 'circularUser',
                    standardSize: 20
                  }}
                />
                <div className="userDetails">
                  <div className="userName">{user.firstName}</div>
                  <div className="userEmail">{user.email}</div>
                </div>
              </div>

              <NudosButton
                buttonText={userIsResponsible ? 'Eliminar' : 'Asignar'}
                handleClick={handleClick}
                customClassName={`assignButton ${userIsResponsible ? 'assigned' : ''}`}
                buttonIcon={<BordOneToneIcon variant={userIsResponsible ? 'trash' : 'add'} customWidth={'10px'} />}
                iconPosition="right"
                isButtonLoading={isLoading}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BordResponsibleUserSearchDropdown;

export interface BordResponsibleUserSearchDropdownProps {
  users: IbackofficeUserData[];
  isLoading?: boolean;
  responsibleUsersList: IResponsibleUserData[];
  handleAssignResponsible: (user: IbackofficeUserData) => Promise<void>;
  handleDeleteResponsible: (responsibleId?: number, userName?: string) => Promise<void>;
  handleCloseDropdown: () => void;
  logisticServiceId?: number | null;
  orderId?: number | null;
}
